/** @format */

import { Component, inject, input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProcessedNonBundleProduct, ProductSku } from '@shared/data-access/interfaces';
import { GoogleAdManagerComponent } from '@shared/components';
import { selectKillswitch, selectPromotionData } from '@pdp/data-access/state';
import { BNPLComponent } from '../buy-now-pay-later/bnpl.component';
import { PromotionComponent } from '../promotion/promotion.component';
import { ReviewRatingSummaryStarsComponent } from '@product-reviews/components/review-rating-summary-stars';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PriceComponent } from '../price/price.component';
import { HybridPlaAttributeThumbnailsComponent } from '../hybrid-pla-attribute-thumbnails/hybrid-pla-attribute-thumbnails.component';

@Component({
	selector: 'pdp-hybrid-pla-product-details',
	imports: [
		BNPLComponent,
		CommonModule,
		GoogleAdManagerComponent,
		HybridPlaAttributeThumbnailsComponent,
		PriceComponent,
		PromotionComponent,
		ReviewRatingSummaryStarsComponent
	],
	templateUrl: './hybrid-pla-product-details.component.html',
	styleUrl: './hybrid-pla-product-details.component.scss'
})
export class HybridPlaProductDetailsComponent implements OnInit {
	private readonly store = inject(Store);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);

	readonly product = input.required<ProcessedNonBundleProduct>();

	readonly promotions = this.store.selectSignal(selectPromotionData);

	readonly enableTooltipSurvey = this.store.selectSignal(selectKillswitch('enableTooltipSurvey'));
	readonly isRibbonGAMBannerEnabled = this.store.selectSignal(
		selectKillswitch('isRibbonGAMBannerEnabled')
	);
	readonly reviewsOff = this.store.selectSignal(selectKillswitch('reviewsOff'));

	basePDPUrl: string;

	resolvedSku: ProductSku;

	ngOnInit() {
		const queryParams = this.route.snapshot.queryParams;

		this.basePDPUrl = this.buildBasePDPUrl(queryParams);

		this.resolvedSku = this.resolveSkuFromQueryParams(queryParams['sku'], queryParams['color']);
	}

	private buildBasePDPUrl(queryParams: Params) {
		const filteredParams: Params = {};

		['color', 'sku'].forEach((param) => {
			if (typeof queryParams[param] !== 'undefined') {
				filteredParams[param] = queryParams[param];
			}
		});

		const urlTree = this.router.createUrlTree([], {
			relativeTo: this.route,
			queryParams: filteredParams
		});

		return this.router.serializeUrl(urlTree);
	}

	private resolveSkuFromQueryParams(sku: string, color: string) {
		if (sku) {
			return this.product().skus.find((productSku) => productSku.partNumber === sku);
		}

		if (color) {
			// consider single-value attributes pre-selected
			const singleValueAttributes =
				this.product().definingAttributes?.flatMap((attr) =>
					attr.value.split('|').length === 1 ? [attr.name] : []
				) || [];

			return this.product().skus.find((productSku) => {
				if (!productSku.definingAttributes || productSku.definingAttributes.length === 0) {
					return;
				}

				// match sku if all defining attributes are either single-value attributes or match the color
				return productSku.definingAttributes.every((attr) => {
					if (singleValueAttributes.includes(attr.name)) {
						return true;
					}

					return (
						attr.name.toLowerCase() === 'color' &&
						attr.value.toLowerCase() === color.toLowerCase()
					);
				});
			});
		}
	}
}
